import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './curl-converter.component.module.scss';

type Target = 'fetch' | 'httpie' | 'python' | 'wget';

const SLUG_TO_TARGET: Record<string, Target> = {
    'convert-curl-to-fetch': 'fetch',
    'convert-curl-to-httpie': 'httpie',
    'convert-curl-to-python': 'python',
    'convert-curl-to-wget': 'wget',
};

const TARGET_LABEL: Record<Target, string> = {
    fetch: 'JavaScript Fetch',
    httpie: 'HTTPie',
    python: 'Python (requests)',
    wget: 'wget',
};

const SAMPLE = `curl -X POST https://api.example.com/v1/users \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer abc123" \\
  -d '{"name": "Ada", "email": "ada@example.com"}'`;

interface ParsedCurl {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: string | null;
    auth: { user: string; pass: string } | null;
}

interface State {
    target: Target;
    input: string;
    error: string | null;
    copied: boolean;
}

export default class CurlConverterComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const target = SLUG_TO_TARGET[props.feature || ''] || 'fetch';
        this.state = { target, input: SAMPLE, error: null, copied: false };
    }

    private parseCurl(raw: string): ParsedCurl | null {
        if (!raw.trim()) return null;
        // Strip line continuations and normalize whitespace
        const cleaned = raw.replace(/\\\s*\n\s*/g, ' ').trim();
        // Tokenize respecting single + double quotes
        const tokens: string[] = [];
        let cur = '';
        let i = 0;
        let quote: string | null = null;
        while (i < cleaned.length) {
            const c = cleaned[i];
            if (quote) {
                if (c === quote) { quote = null; i++; continue; }
                cur += c; i++;
            } else if (c === '"' || c === "'") {
                quote = c; i++;
            } else if (/\s/.test(c)) {
                if (cur) { tokens.push(cur); cur = ''; }
                i++;
            } else {
                cur += c; i++;
            }
        }
        if (cur) tokens.push(cur);
        if (tokens.length < 2 || tokens[0] !== 'curl') return null;

        const result: ParsedCurl = { method: 'GET', url: '', headers: {}, body: null, auth: null };
        for (let j = 1; j < tokens.length; j++) {
            const t = tokens[j];
            if (t === '-X' || t === '--request') {
                result.method = tokens[++j].toUpperCase();
            } else if (t === '-H' || t === '--header') {
                const h = tokens[++j];
                const sep = h.indexOf(':');
                if (sep > 0) result.headers[h.slice(0, sep).trim()] = h.slice(sep + 1).trim();
            } else if (t === '-d' || t === '--data' || t === '--data-raw' || t === '--data-binary') {
                result.body = tokens[++j];
                if (result.method === 'GET') result.method = 'POST';
            } else if (t === '-u' || t === '--user') {
                const v = tokens[++j];
                const sep = v.indexOf(':');
                result.auth = sep > 0 ? { user: v.slice(0, sep), pass: v.slice(sep + 1) } : { user: v, pass: '' };
            } else if (t === '-A' || t === '--user-agent') {
                result.headers['User-Agent'] = tokens[++j];
            } else if (t === '-e' || t === '--referer') {
                result.headers['Referer'] = tokens[++j];
            } else if (!t.startsWith('-') && !result.url) {
                result.url = t;
            }
        }
        return result.url ? result : null;
    }

    private toFetch(p: ParsedCurl): string {
        const opts: string[] = [`  method: ${JSON.stringify(p.method)}`];
        const headers = { ...p.headers };
        if (p.auth) headers['Authorization'] = `Basic ${typeof btoa !== 'undefined' ? btoa(`${p.auth.user}:${p.auth.pass}`) : `<base64('${p.auth.user}:${p.auth.pass}')>`}`;
        if (Object.keys(headers).length) {
            opts.push(`  headers: ${JSON.stringify(headers, null, 4).replace(/\n/g, '\n  ')}`);
        }
        if (p.body !== null) opts.push(`  body: ${JSON.stringify(p.body)}`);
        return `fetch(${JSON.stringify(p.url)}, {\n${opts.join(',\n')}\n})\n  .then(r => r.json())\n  .then(data => console.log(data));`;
    }

    private toHttpie(p: ParsedCurl): string {
        const parts: string[] = ['http'];
        if (p.method !== 'GET') parts.push(p.method);
        else if (p.body !== null) parts.push('POST'); // implicit
        parts.push(`'${p.url}'`);
        for (const [k, v] of Object.entries(p.headers)) {
            parts.push(`'${k}:${v}'`);
        }
        if (p.body !== null) {
            parts.push(`<<<'${p.body.replace(/'/g, `'\\''`)}'`);
        }
        if (p.auth) parts.push(`-a ${p.auth.user}:${p.auth.pass}`);
        return parts.join(' \\\n  ');
    }

    private toPython(p: ParsedCurl): string {
        const lines: string[] = ['import requests', ''];
        const headers = { ...p.headers };
        const auth = p.auth ? `, auth=(${JSON.stringify(p.auth.user)}, ${JSON.stringify(p.auth.pass)})` : '';
        const args: string[] = [`'${p.url}'`];
        if (Object.keys(headers).length) {
            args.push(`headers=${JSON.stringify(headers, null, 4).replace(/\n/g, '\n    ')}`);
        }
        if (p.body !== null) {
            // Try to parse as JSON
            try {
                const j = JSON.parse(p.body);
                args.push(`json=${JSON.stringify(j, null, 4).replace(/\n/g, '\n    ')}`);
            } catch {
                args.push(`data=${JSON.stringify(p.body)}`);
            }
        }
        const fn = p.method.toLowerCase() === 'get' ? 'get'
            : p.method.toLowerCase() === 'post' ? 'post'
            : p.method.toLowerCase() === 'put' ? 'put'
            : p.method.toLowerCase() === 'delete' ? 'delete'
            : p.method.toLowerCase() === 'patch' ? 'patch'
            : 'request';
        const callArgs = fn === 'request' ? `'${p.method}', ${args.join(', ')}` : args.join(', ');
        lines.push(`response = requests.${fn}(${callArgs}${auth})`);
        lines.push('print(response.status_code)');
        lines.push('print(response.text)');
        return lines.join('\n');
    }

    private toWget(p: ParsedCurl): string {
        const parts: string[] = ['wget'];
        if (p.method !== 'GET') {
            parts.push(`--method=${p.method}`);
        }
        for (const [k, v] of Object.entries(p.headers)) {
            parts.push(`--header='${k}: ${v}'`);
        }
        if (p.body !== null) {
            parts.push(`--body-data='${p.body.replace(/'/g, `'\\''`)}'`);
        }
        if (p.auth) {
            parts.push(`--user='${p.auth.user}'`);
            parts.push(`--password='${p.auth.pass}'`);
        }
        parts.push('-q -O -');
        parts.push(`'${p.url}'`);
        return parts.join(' \\\n  ');
    }

    private get output(): string {
        const parsed = this.parseCurl(this.state.input);
        if (!parsed) return '';
        switch (this.state.target) {
            case 'fetch': return this.toFetch(parsed);
            case 'httpie': return this.toHttpie(parsed);
            case 'python': return this.toPython(parsed);
            case 'wget': return this.toWget(parsed);
        }
    }

    private setTarget = (target: Target) => this.setState({ target, copied: false });

    copyOutput = () => {
        const out = this.output;
        if (!out) return;
        navigator.clipboard.writeText(out).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { target, input, copied } = this.state;
        const parsed = this.parseCurl(input);
        const out = this.output;
        const noCurl = !parsed && input.trim().length > 0;

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.targetTabs} role="tablist" aria-label="Output target">
                            {(Object.keys(TARGET_LABEL) as Target[]).map(t => (
                                <button key={t}
                                    role="tab"
                                    aria-selected={target === t}
                                    className={`${styles.targetTab} ${target === t ? styles.targetTabActive : ''}`}
                                    onClick={() => this.setTarget(t)}>
                                    {TARGET_LABEL[t]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>cURL command</label>
                        <textarea className={sharedStyles.textarea} rows={14} value={input}
                            spellCheck={false}
                            onChange={e => this.setState({ input: e.target.value, copied: false })}
                            aria-label="cURL command" />
                        {noCurl && <p className={styles.warning}>Couldn't parse this as a cURL command. Make sure it starts with <code>curl</code>.</p>}
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>{TARGET_LABEL[target]} output</label>
                        <textarea className={sharedStyles.textarea} readOnly rows={14} value={out}
                            placeholder="Paste a cURL command on the left to see the converted output."
                            aria-label={`${TARGET_LABEL[target]} output`} />
                        <div className={styles.actions}>
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput} disabled={!out}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
